import { SeamlessApiClient } from "@mondaydotcomorg/api";

const mondayApi = new SeamlessApiClient();
const unwrapResponse = (response) =>
  response?.data?.data ?? response?.data ?? response;

const executeGraphQl = async (query, variables) => {
  try {
    const response = await mondayApi.request(query, variables);
    const data = unwrapResponse(response);
    if (!data) {
      throw new Error("The monday.com API returned no data.");
    }

    return data;
  } catch (error) {
    console.error("monday.com API request failed.", error);
    throw error;
  }
};

export const getItem = async (itemId) => {
  const response = await executeGraphQl(`
    query {
      items (ids: [${itemId}]) {
        id
        name
        column_values {
          id
          value
          type
          text
          column {
            id
            title
          }
        }
      }
    }
  `);

  return response.items[0];
};

export const getBoardColumns = async (boardId) => {
  if (!boardId) {
    throw new Error("Missing boardId to load columns.");
  }

  const response = await executeGraphQl(`
    query getBoardColumns($boardIds: [ID!]) {
      boards(ids: $boardIds) {
        id
        columns {
          id
          title
          type
        }
      }
    }
  `, { boardIds: [boardId] });

  const [board] = response?.boards || [];
  return board?.columns || [];
};

export const getBoardGroups = async (boardId) => {
  if (!boardId) {
    throw new Error("Missing boardId to load groups.");
  }

  const response = await executeGraphQl(`
    query getBoardGroups($boardIds: [ID!]) {
      boards(ids: $boardIds) {
        id
        groups {
          id
          title
        }
      }
    }
  `, { boardIds: [boardId] });

  const [board] = response?.boards || [];
  return board?.groups || [];
};

export const createBoardColumn = async (boardId, title, columnType) => {
  const response = await executeGraphQl(`
    mutation createColumn($boardId: ID!, $title: String!, $columnType: ColumnType!) {
      create_column(board_id: $boardId, title: $title, column_type: $columnType) {
        id
        title
        type
      }
    }
  `, { boardId, title, columnType });

  return response?.create_column;
};

export const createOrGetTag = async (boardId, tagName) => {
  const response = await executeGraphQl(`
    mutation createOrGetTag($boardId: ID!, $tagName: String!) {
      create_or_get_tag(board_id: $boardId, tag_name: $tagName) {
        id
        name
      }
    }
  `, { boardId, tagName });

  return response?.create_or_get_tag;
};

export const createBoardView = async (boardId, viewName, filter) => {
  const response = await executeGraphQl(`
    mutation createBoardView($boardId: ID!, $viewName: String!, $filter: ItemsQueryGroup) {
      create_view(board_id: $boardId, type: TABLE, name: $viewName, filter: $filter) {
        id
        name
      }
    }
  `, { boardId, viewName, filter });

  return response?.create_view;
};

export const setTagColumnValue = async (boardId, itemId, columnId, tagIds) => {
  if (!boardId || !itemId || !columnId) {
    throw new Error("Missing required parameters to update tag column value.");
  }

  const normalizedTagIds = (Array.isArray(tagIds) ? tagIds : [tagIds])
    .map((tagId) => {
      const numeric = Number(tagId);
      return Number.isFinite(numeric) ? numeric.toString() : String(tagId);
    })
    .filter(Boolean);

  if (!normalizedTagIds.length) {
    throw new Error("At least one tag id is required to update the column.");
  }

  const columnValues = {
    [columnId]: {
      tag_ids: normalizedTagIds
    }
  };
  const columnValuesString = JSON.stringify(columnValues).replace(/"/g, '\\"');

  const response = await executeGraphQl(`
    mutation setTagColumnValue($boardId: ID!, $itemId: ID!) {
      change_multiple_column_values(
        board_id: $boardId,
        item_id: $itemId,
        column_values: "${columnValuesString}"
      ) {
        id
      }
    }
  `, { boardId, itemId });

  return response?.change_multiple_column_values;
};

export const updateColumnValue = async (boardId, itemId, columnId, value) => {
  if (!boardId || !itemId || !columnId) {
    throw new Error("Missing required parameters to update column value.");
  }

  let values = {
    [columnId]: value
  };

  let valuesQuery = "{";

  for (var prop in values) {
    let value = JSON.stringify(JSON.stringify(values[prop]));
    value = value.substring(3, value.length-3)

    if(valuesQuery.length > 2) {
      valuesQuery+= ", ";
    }
    valuesQuery += `\\\"${prop}\\\" : \\\"${value}\\\"`;
  }

  valuesQuery+= "}";

  const response = await executeGraphQl(`
    mutation change_column_values($boardId: ID!, $itemId: ID!) {
      change_multiple_column_values(board_id: $boardId, item_id: $itemId, column_values: "${valuesQuery}") {
        id
      }
    }
  `, { boardId, itemId });

  return response.change_simple_column_value;
};
